import { useContext } from 'react';
import root from 'react-shadow';
import { useParams } from 'react-router';
import { i18n } from 'app/utils/i18n';
import { useCss } from 'app/api/style-queries';
import { useSelectedTemplates } from 'app/api/template-queries';
import { useGetDocument, useGetDocumentTableOfContent } from 'app/api/documents/document-queries';
import { UserContext } from 'app/state/contexts';
import { HoveredTarget, buildHoverModifier } from '../utils/builtin-selectors';
import { HIGHLIGHT_CSS, PreviewHeader, SAMPLE_HEADINGS, renderTocEntry } from './preview-markup';

interface TocPreviewProps {
  hoveredInner?: HoveredTarget | null;
}

export function TocPreview({ hoveredInner }: TocPreviewProps = {}) {
  const { documentId } = useParams<{ documentId: string }>();
  const { selectedOrganization } = useContext(UserContext);
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const { data: selectedTemplates } = useSelectedTemplates(documentId);
  const css = useCss({ documentSuperId: documentId, documentRevisionId: undefined, useDraft: true, withStyle: undefined, embedd: undefined });

  const docContent: any = pDocument.data?.content ?? {};
  const metadataTexts: Record<string, Record<string, string>> = docContent.metadata?.texts ?? {};
  const previewLocale = Object.keys(metadataTexts)[0];
  const tocQuery = useGetDocumentTableOfContent(pDocument.data?.revisionId ?? null, { locale: previewLocale });

  if (css.isLoading || !css.data || !pDocument.data) {
    return (
      <div
        className="toc-preview text-muted fs-7 d-flex align-items-center justify-content-center"
        style={{ height: 140, border: '1px dashed #dee2e6', borderRadius: 4 }}
      >
        {i18n('common.loading')}
      </div>
    );
  }

  const templateSuperId = selectedTemplates?.[0] ?? '';
  const realHeaders = (tocQuery.data?.content as PreviewHeader[] | undefined) ?? undefined;
  const useReal = realHeaders && realHeaders.length > 0;
  const headers: PreviewHeader[] = useReal ? realHeaders! : SAMPLE_HEADINGS;

  const hoverModifier = buildHoverModifier('editor-table-of-content--container', hoveredInner);

  return (
    <>
      {!useReal && <p className="text-muted fs-7 fst-italic mb-1">{i18n('dc.preview.toc-sample-notice')}</p>}
      <div className="toc-preview mb-3" style={{ border: '1px solid #dee2e6', borderRadius: 4, maxHeight: '350px', overflowY: 'auto', overflowX: 'auto' }}>
        <root.div>
          <style>{css.data.shadowedCss}</style>
          <style>{HIGHLIGHT_CSS}</style>
          <div
            className={`page-content xrp-template-${templateSuperId}`}
            style={{ position: 'relative', width: '100%', height: 'auto', minHeight: 300, padding: '12px 20px', overflow: 'visible', boxSizing: 'border-box' }}
          >
            <div className={`editor-table-of-content--container ${hoverModifier}`.trim()}>
              <div className="editor-table-of-content--content">
                <ol>{headers.map((h, i) => renderTocEntry(h, i, !useReal))}</ol>
              </div>
            </div>
          </div>
        </root.div>
      </div>
    </>
  );
}
